/**
 * /contact — public contact form. Posts to /api/contact which routes the
 * message to the school's inbox via Resend, with the visitor's address set
 * as Reply-To so a one-click Gmail Reply goes straight back to them.
 *
 * Includes a hidden honeypot field (`website`). Real browsers leave it empty;
 * naive spam bots fill every input. The backend silently drops on hit.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../api";
import { useI18n } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  en: {
    eyebrow: "Contact us",
    headline: "We'd love to hear from you.",
    lead:
      "Questions about a lesson, a small group, or an immersion week? Send us a note and we'll come back to you within one or two working days.",
    name: "Your name",
    email: "Your email",
    message: "Your message",
    placeholderName: "Maria Gonzalez",
    placeholderEmail: "you@example.com",
    placeholderMessage: "Tell us a little about what brought you here…",
    submit: "Send message",
    sending: "Sending…",
    successHeadline: "Message sent.",
    successBody:
      "Thank you — we've passed your note along and replied with a quick confirmation to your inbox. We'll be in touch shortly.",
    successAgain: "Send another",
    minMessage: "Please write a message of at least 10 characters.",
    failGeneric: "Something went wrong sending your message. Please try again, or write to us directly at info@thinkspanish.co.uk.",
    failService: "Our email service is briefly unavailable. Please try again in a minute, or write to us directly at info@thinkspanish.co.uk.",
  },
  es: {
    eyebrow: "Contacto",
    headline: "Nos encantaría saber de ti.",
    lead:
      "¿Preguntas sobre una clase, un grupo reducido o una semana de inmersión? Escríbenos y te responderemos en uno o dos días laborables.",
    name: "Tu nombre",
    email: "Tu correo electrónico",
    message: "Tu mensaje",
    placeholderName: "María González",
    placeholderEmail: "tu@ejemplo.com",
    placeholderMessage: "Cuéntanos brevemente qué te ha traído hasta aquí…",
    submit: "Enviar mensaje",
    sending: "Enviando…",
    successHeadline: "Mensaje enviado.",
    successBody:
      "Gracias — hemos pasado tu nota y te hemos enviado una confirmación rápida. Te contestaremos en breve.",
    successAgain: "Enviar otro",
    minMessage: "Por favor, escribe un mensaje de al menos 10 caracteres.",
    failGeneric: "Algo salió mal al enviar tu mensaje. Inténtalo de nuevo o escríbenos directamente a info@thinkspanish.co.uk.",
    failService: "Nuestro servicio de correo no está disponible en este momento. Inténtalo de nuevo en un minuto o escríbenos directamente a info@thinkspanish.co.uk.",
  },
};

export default function Contact() {
  const { lang } = useI18n();
  const c = COPY[lang === "es" ? "es" : "en"];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (message.trim().length < 10) {
      setError(c.minMessage);
      return;
    }
    setLoading(true);
    try {
      await api.submitContact({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        message: message.trim(),
        website,
      });
      setDone(true);
      toast.success(c.successHeadline);
    } catch (err) {
      const msg = String(err?.message || "");
      // Backend 503 → service-specific copy; anything else → generic
      if (/unavailable/i.test(msg) || /503/.test(msg)) {
        setError(c.failService);
      } else {
        setError(c.failGeneric);
      }
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setName("");
    setEmail("");
    setMessage("");
    setWebsite("");
    setError("");
    setDone(false);
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-20 px-6 md:px-10"
      data-testid="contact-page"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/55 mb-4">
            {c.eyebrow}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter font-light text-[#1C1C1C]">
            {c.headline}
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg text-[#1C1C1C]/70 leading-relaxed">
            {c.lead}
          </p>
        </motion.div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-12 bg-white border border-[#1C1C1C]/10 rounded-2xl p-8 md:p-10"
            data-testid="contact-success"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] tracking-[0.22em] uppercase font-medium mb-4">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {c.successHeadline}
            </div>
            <p className="text-[#1C1C1C]/80 leading-relaxed">
              {c.successBody}
            </p>
            <button
              onClick={reset}
              data-testid="contact-success-again"
              className="mt-6 px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              {c.successAgain}
            </button>
          </motion.div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-12 bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-10 space-y-5"
            data-testid="contact-form"
          >
            {/* Honeypot — visually hidden, screen-reader hidden, no autofill */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="contact-honeypot"
                />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field label={c.name}>
                <input
                  required
                  maxLength={120}
                  autoComplete="name"
                  placeholder={c.placeholderName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  data-testid="contact-name"
                />
              </Field>
              <Field label={c.email}>
                <input
                  required
                  type="email"
                  autoComplete="email"
                  placeholder={c.placeholderEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  data-testid="contact-email"
                />
              </Field>
            </div>

            <Field label={c.message}>
              <textarea
                required
                rows={7}
                maxLength={5000}
                placeholder={c.placeholderMessage}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input resize-y"
                data-testid="contact-message"
              />
              <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40 text-right">
                {message.length} / 5000
              </div>
            </Field>

            {error && (
              <div
                className="text-sm text-[#B83A3A] leading-relaxed"
                data-testid="contact-error"
              >
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="px-7 py-4 bg-[#1C1C1C] text-[#FAF9F6] rounded-full text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors inline-flex items-center gap-3"
                data-testid="contact-submit"
              >
                {loading ? c.sending : c.submit}
                <span className="text-base">→</span>
              </button>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/45">
                info@thinkspanish.co.uk
              </span>
            </div>
          </form>
        )}

        <style>{`.input{width:100%;padding:.85rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.85rem;font-size:1rem;line-height:1.55;outline:none;font-family:Georgia,'Times New Roman',serif}.input:focus{border-color:rgba(28,28,28,0.4);background:white}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
